import { Avatar, Box, CardHeader, DialogContent, Divider } from '@mui/material';
import Devices from '@mui/icons-material/Devices';
import LocalOffer from '@mui/icons-material/LocalOffer';

import {
  AppLogoImage,
  BenefitsStatusIcon,
  ContainedText,
} from '@orcha/micro-library-core-ui';
import { BenefitsBanner } from '@orcha/micro-library-app-details-ui';
import Image from 'next/image';
import IPlatform from '@orcha/types/platform';
import theme from 'src/themes/theme';
import { useSiteContext } from 'src/store';
import useTranslation from 'next-translate/useTranslation';
import DownloadDialogProps from './DownloadDialog.types';
import DownloadDialogActions from './DownloadDialogActions';
import Dialog from '../Dialog';

const fallbackLogoImageURL = '/app_logo_fallback.jpg';

export const DownloadDialog = (props: DownloadDialogProps) => {
  const {
    handleClose,
    app: { name, cost, logoUrl, platforms, appStatus },
    ...rest
  } = props;

  const { t } = useTranslation('appCommon');
  const { showCommissionableStatus } = useSiteContext();
  const isAppCommissioned = appStatus?.status === 'Commissioned';

  const isAppPromo = appStatus?.status === 'Promo';

  const isAppCommissionable =
    showCommissionableStatus && appStatus?.status === 'Commissionable';

  const hideCost =
    (isAppCommissionable &&
      appStatus?.detailedStatus === 'CommissionableOnly') ||
    isAppCommissioned ||
    isAppPromo;

  return (
    <Dialog
      handleClose={handleClose}
      maxWidth="md"
      fullWidth
      dialogTestId="download-dialog"
      closeButtonTestId="download-dialog-close-button"
      title={t('downloadDialog.title')}
      subtitle={t('downloadDialog.subtitle')}
      {...rest}
    >
      <Divider />
      <DialogContent
        sx={{
          padding: '0',
        }}
      >
        <CardHeader
          sx={{
            '& .MuiCardHeader-action': {
              marginRight: 0,
            },
          }}
          titleTypographyProps={{
            sx: {
              lineHeight: '24px',
              fontWeight: '700',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            },
          }}
          title={name}
          avatar={
            logoUrl && (
              <Avatar variant="rounded">
                <AppLogoImage
                  fallbackLogoImageURL={fallbackLogoImageURL}
                  appName={name}
                  appLogoUrl={logoUrl}
                  quality={50}
                  priority
                  width="40"
                  height="40"
                  FlexibleImageComponent={Image}
                />
              </Avatar>
            )
          }
        />
      </DialogContent>
      <Divider />
      <DialogContent sx={{ padding: theme.spacing(2) }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Devices fontSize="small" color="secondary" />
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {platforms?.map((platform: IPlatform) => {
                return (
                  <ContainedText key={platform.platformName}>
                    {platform.platformName}
                  </ContainedText>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(2),
            }}
          >
            {(isAppCommissionable || isAppCommissioned) && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BenefitsStatusIcon
                    status={appStatus.status}
                    label={t(`microsite:appCostLabels.${appStatus.status}`)}
                  />
                </Box>
              </>
            )}

            {isAppPromo && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BenefitsStatusIcon
                    status={appStatus.status}
                    label={t(
                      `microsite:appCostLabels.${appStatus.detailedStatus}`
                    )}
                  />
                </Box>
              </>
            )}

            {cost && !hideCost && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box
                  sx={{ display: 'flex', alignItems: 'center' }}
                  data-testid="download-dialog-cost-container"
                >
                  <ContainedText
                    icon={<LocalOffer fontSize="small" color="secondary" />}
                  >
                    {cost}
                  </ContainedText>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      {isAppCommissioned && (
        <DialogContent
          sx={{
            padding: theme.spacing(2),
          }}
        >
          <BenefitsBanner
            bannerState="Commissioned"
            title={t('microsite:appBenefitsBanner.title.Commissioned')}
            body={t('microsite:appBenefitsBanner.bodyText.Commissioned')}
          />
        </DialogContent>
      )}

      {(isAppCommissionable || isAppPromo) && (
        <DialogContent
          sx={{
            padding: theme.spacing(2),
          }}
        >
          <BenefitsBanner
            bannerState={appStatus.status}
            title={t(
              `microsite:appBenefitsBanner.title.${appStatus.detailedStatus}`
            )}
            body={t(
              `microsite:appBenefitsBanner.bodyText.${appStatus.detailedStatus}`
            )}
          />
        </DialogContent>
      )}

      <DialogContent sx={{ padding: theme.spacing(2) }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <DownloadDialogActions appData={props.app} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadDialog;
